import { useEffect, useState } from "react";
import { useSiteClub } from "../lib/SiteClubContext";
import { listLocations, type LocationRow } from "../lib/api";
import { LocationCard } from "../components/LocationCard";

/** Lower-cased, whitespace-collapsed text for case-insensitive matching. */
function norm(s: string | undefined): string {
  return (s ?? "").toLowerCase().replace(/\s+/g, " ").trim();
}

/** Every word in the query has to appear in the track's name or address
 *  (which includes city, state and zip code). */
function matchesQuery(l: LocationRow, query: string): boolean {
  const words = norm(query).split(" ").filter(Boolean);
  if (words.length === 0) return true;
  const hay = norm(`${l.name} ${l.address ?? ""}`);
  return words.every((w) => hay.includes(w));
}

export default function LocationsPage() {
  const [locations, setLocations] = useState<LocationRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { club, loading: clubLoading } = useSiteClub();
  const [query, setQuery] = useState("");

  // On a club's own site, only that club's tracks; on the service site, every track.
  useEffect(() => {
    if (clubLoading) return;
    listLocations("track", club?.clubId)
      .then((res) => setLocations(res.locations))
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load tracks."));
  }, [club?.clubId, clubLoading]);

  const shown = locations?.filter((l) => matchesQuery(l, query)) ?? null;

  return (
    <div>
      <h1>Tracks</h1>
      {locations && locations.length > 0 && (
        <div className="row event-filters" role="search">
          <input
            type="search"
            placeholder="Search by name, address or zip code"
            aria-label="Search tracks"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      )}
      {error && <p className="err">{error}</p>}
      {!locations && !error && <p className="muted">Loading…</p>}
      {locations && locations.length === 0 && <p className="muted">No tracks listed yet.</p>}
      {shown && locations && locations.length > 0 && shown.length === 0 && (
        <p className="muted">No tracks match “{query.trim()}”.</p>
      )}
      <div className="events-grid">
        {shown?.map((l) => (
          <LocationCard key={l.locationId} location={l} />
        ))}
      </div>
    </div>
  );
}
